import { LegalLayout, LegalSection, ContactEmail } from "../../components/LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy | EnquiryOps"
      description="How EnquiryOps collects, uses, and protects personal data under Singapore's PDPA, and under GDPR/CCPA where applicable."
      heading="Privacy Policy"
      updated="19 July 2026"
    >
      <LegalSection title="Scope">
        <p>
          This policy applies to all users of EnquiryOps. EnquiryOps is based in Singapore, and Singapore's
          Personal Data Protection Act (PDPA) is the primary framework governing how we handle personal data.
          Additional rights are described below for users in the EU/EEA (under the GDPR) and California (under
          the CCPA).
        </p>
      </LegalSection>

      <LegalSection title="Information We Collect">
        <p>We collect the following categories of information:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Account data: your name, business name, email address, and contact details when you create an account or place a deposit.</li>
          <li>Usage data: how you interact with the platform, such as pages visited and features used.</li>
          <li>Enquiry and message content: parent enquiries, messages, and booking details that we process on behalf of the centres that use EnquiryOps.</li>
        </ul>
      </LegalSection>

      <LegalSection title="How We Use It / Purpose Limitation">
        <p>
          Personal data is used only for the purpose disclosed at the time of collection, such as operating your
          account, responding to enquiries, running follow-up workflows on behalf of your centre, and improving
          the service. We do not sell personal data.
        </p>
      </LegalSection>

      <LegalSection title="Consent">
        <p>
          Under the PDPA, we notify you of the purpose and obtain your consent before collecting, using, or
          disclosing your personal data. You may withdraw consent at any time by giving us reasonable notice at
          <> </>
          <ContactEmail email="privacy@getenquiryops.com" />. Withdrawing consent may affect our ability to provide certain services to you. GDPR
          users: see Legal Bases for Processing below.
        </p>
      </LegalSection>

      <LegalSection title="Legal Bases for Processing (GDPR, for EU/EEA users)">
        <p>Where the GDPR applies, we process personal data on the following legal bases:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Contract: processing needed to provide the service you signed up for.</li>
          <li>Consent: where you have given specific consent, for example to marketing messages.</li>
          <li>Legitimate interest: to secure, improve, and support the service in ways you would reasonably expect.</li>
        </ul>
      </LegalSection>

      <LegalSection title="Your Rights">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            PDPA (all users): Access and Correction. You may request access to your personal data in our
            possession, and request correction of any error or omission.
          </li>
          <li>
            GDPR (EU/EEA users): access, correction, deletion, portability, and objection to processing.
          </li>
          <li>
            CCPA (California users): the right to know what personal information we collect, the right to
            delete it, and the right to opt out of the sale or sharing of personal information ("Do Not Sell or
            Share My Info"). We do not sell personal information.
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact <ContactEmail email="privacy@getenquiryops.com" />.
        </p>
      </LegalSection>

      <LegalSection title="Accuracy">
        <p>
          We take reasonable steps to ensure that personal data collected by us or on our behalf is accurate and
          complete, especially where it is likely to be used to make a decision that affects you.
        </p>
      </LegalSection>

      <LegalSection title="Protection">
        <p>
          We make reasonable security arrangements to protect personal data in our possession against
          unauthorised access, collection, use, disclosure, copying, modification, or disposal.
        </p>
      </LegalSection>

      <LegalSection title="Retention Limitation">
        <p>
          Personal data is retained only for as long as it is necessary for business or legal purposes. When it
          is no longer needed, it is securely deleted or anonymised.
        </p>
      </LegalSection>

      <LegalSection title="Transfer Limitation / International Data Transfers">
        <p>
          Where personal data is transferred outside Singapore, or outside the EU/EEA for GDPR purposes, we
          ensure it is protected to a standard comparable to the protection under this policy and applicable
          law, including through contractual safeguards with our service providers.
        </p>
      </LegalSection>

      <LegalSection title="Data Breach Notification">
        <p>
          In the event of a data breach that is likely to result in significant harm to affected individuals, or
          that affects 500 or more individuals, we will notify the Personal Data Protection Commission (PDPC)
          and affected individuals as required by law.
        </p>
      </LegalSection>

      <LegalSection title="Cookies and Tracking">
        <p>
          We use cookies and similar technologies to keep you signed in, remember preferences, and understand
          how the site is used so we can improve it. You can control cookies through your browser settings.
          Disabling cookies may affect some features of the site.
        </p>
      </LegalSection>

      <LegalSection title="Children's Privacy">
        <p>
          EnquiryOps is a business tool and is not directed at children under 13 (or under 16 where that higher
          age applies). We do not knowingly collect personal data directly from children. Enquiry records
          created by centres may reference a child's first name and level for the purpose of managing a
          parent's enquiry; this data is processed on behalf of the centre under this policy.
        </p>
      </LegalSection>

      <LegalSection title="Data Protection Officer">
        <p>
          Our designated Data Protection Officer is Paul, contactable at <ContactEmail email="privacy@getenquiryops.com" />. This designation is
          made publicly available in accordance with PDPA requirements.
        </p>
      </LegalSection>

      <LegalSection title="Changes to This Policy">
        <p>
          We may update this policy from time to time. Material changes will be posted on this page with an
          updated date. Continued use of the service after changes take effect means you accept the updated
          policy.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Questions about this policy or our data practices: <ContactEmail email="privacy@getenquiryops.com" />.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
